import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Clock, MessageSquare, TrendingUp, Users, UserX, Video } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { apiClient } from '~/lib/api';
import { formatNumber } from '~/lib/utils';

export default function AnalyticsPage() {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => apiClient.getAnalytics(),
    refetchInterval: 30000, // Refetch every 30 seconds for real-time data
  });

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-3xl font-bold'>Analytics</h1>
          <p className='text-muted-foreground'>Platform overview and statistics</p>
        </div>
        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Card key={i}>
              <CardHeader className='pb-3'>
                <div className='h-4 w-24 bg-muted animate-pulse rounded' />
              </CardHeader>
              <CardContent>
                <div className='h-8 w-32 bg-muted animate-pulse rounded' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const stats = analytics?.data;

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      description: `+${stats?.recentUsers || 0} in last 30 days`,
      color: 'text-blue-600',
    },
    {
      title: 'Active Now',
      value: stats?.activeNow || 0,
      icon: Activity,
      description: 'In last 5 minutes',
      color: 'text-green-600',
      highlight: true,
    },
    {
      title: 'Active Today',
      value: stats?.activeToday || 0,
      icon: TrendingUp,
      description: 'Users today',
      color: 'text-emerald-600',
    },
    {
      title: 'Inactive Users',
      value: stats?.inactiveUsers || 0,
      icon: UserX,
      description: '30+ days inactive',
      color: 'text-orange-600',
    },
    {
      title: 'Total Videos',
      value: stats?.totalVideos || 0,
      icon: Video,
      description: 'In database',
      color: 'text-purple-600',
    },
    {
      title: 'Total Comments',
      value: stats?.totalComments || 0,
      icon: MessageSquare,
      description: `+${stats?.recentComments || 0} in last 30 days`,
      color: 'text-cyan-600',
    },
    {
      title: 'Total Reports',
      value: stats?.totalReports || 0,
      icon: AlertTriangle,
      description: `${stats?.pendingReports || 0} pending`,
      color: 'text-yellow-600',
    },
  ];

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Analytics</h1>
        <p className='text-muted-foreground'>Platform overview and real-time statistics</p>
      </div>

      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
        {statCards.map((stat) => (
          <Card key={stat.title} className={stat.highlight ? 'border-green-500 shadow-md' : ''}>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>{stat.title}</CardTitle>
              <stat.icon className={`h-5 w-5 ${stat.color || 'text-muted-foreground'}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stat.highlight ? 'text-green-600' : ''}`}>
                {formatNumber(stat.value)}
              </div>
              <p className='text-xs text-muted-foreground mt-1'>{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className='grid gap-6 md:grid-cols-2'>
        <Card>
          <CardHeader>
            <CardTitle>User Activity</CardTitle>
            <CardDescription>Real-time user engagement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Activity className='h-4 w-4 text-green-600' />
                  <span className='text-sm text-muted-foreground'>Active Now (5m)</span>
                </div>
                <span className='text-lg font-semibold text-green-600'>{formatNumber(stats?.activeNow || 0)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Clock className='h-4 w-4 text-emerald-600' />
                  <span className='text-sm text-muted-foreground'>Active Today</span>
                </div>
                <span className='text-lg font-semibold text-emerald-600'>{formatNumber(stats?.activeToday || 0)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <UserX className='h-4 w-4 text-orange-600' />
                  <span className='text-sm text-muted-foreground'>Inactive (30d+)</span>
                </div>
                <span className='text-lg font-semibold text-orange-600'>{formatNumber(stats?.inactiveUsers || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Platform Activity</CardTitle>
            <CardDescription>Content engagement metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>New Users (30d)</span>
                <span className='text-lg font-semibold'>{formatNumber(stats?.recentUsers || 0)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>New Comments (30d)</span>
                <span className='text-lg font-semibold'>{formatNumber(stats?.recentComments || 0)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>Pending Reports</span>
                <span className='text-lg font-semibold text-yellow-600'>
                  {formatNumber(stats?.pendingReports || 0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common admin tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              <a
                href='/comments'
                className='flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors'
              >
                <MessageSquare className='h-5 w-5 text-muted-foreground' />
                <div className='flex-1'>
                  <p className='font-medium text-sm'>Moderate Comments</p>
                  <p className='text-xs text-muted-foreground'>Review pending comments</p>
                </div>
              </a>
              <a
                href='/reports'
                className='flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors'
              >
                <AlertTriangle className='h-5 w-5 text-muted-foreground' />
                <div className='flex-1'>
                  <p className='font-medium text-sm'>Handle Reports</p>
                  <p className='text-xs text-muted-foreground'>{stats?.pendingReports || 0} pending reports</p>
                </div>
              </a>
              <a
                href='/users'
                className='flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors'
              >
                <Users className='h-5 w-5 text-muted-foreground' />
                <div className='flex-1'>
                  <p className='font-medium text-sm'>Manage Users</p>
                  <p className='text-xs text-muted-foreground'>View and manage user accounts</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
